<template>
    <div>
        <div class="card card-gg" v-if="user">
            <div class="card-header d-flex align-items-center">
                Logo
            </div>
            <div class="card-body">
                <form role="form">
                    <div class="form-group row">
                        <t-avatar
                            name="photo"
                            v-model="team.photo_url"
                            class="col-md-6 d-flex align-items-center"
                            :remove-url="`/api/teams/${team.id}/settings/photo`"
                            :store-url="`/api/teams/${team.id}/settings/photo`"
                        ></t-avatar>
                    </div>
                </form>
            </div>
        </div>
        <div class="card card-gg">
            <div class="card-header">{{ teamTypeLabel }}</div>
            <div class="card-body">
                <form role="form" @submit.prevent="save">
                    <div class="row">
                        <div class="col-md-6">
                            <!-- Team Name (for advertisers only) -->
                            <div v-if="isAdvertiser" class="form-group">
                                <label for="name">{{ teamNameLabel }}</label>
                                <input type="text" data-test="team-name" class="form-control" id="name"
                                       v-model="form.name" />
                                <form-errors :errors="form.errors" field="name"></form-errors>
                            </div>

                            <!-- Industry (for advertisers only) -->
                            <div v-if="isAdvertiser" class="form-group">
                                <label for="industry_id">Industry</label>
                                <t-form-select
                                    id="industry_id"
                                    name="industry_id"
                                    v-model="form.industry_id"
                                    :options="industryOptions"
                                    data-test="team-industry"
                                    placeholder="Select Industry"
                                >
                                </t-form-select>
                                <form-errors :errors="form.errors" field="industry_id"></form-errors>
                            </div>

                            <!-- Team-specific fields -->
                            <div v-if="!isAdvertiser">
                                <!-- Market -->
                                <div class="form-group">
                                    <label for="market_id">Market</label>
                                    <t-form-select
                                        id="market_id"
                                        name="market_id"
                                        v-model="form.market_id"
                                        :options="marketOptions"
                                        placeholder="Select Market"
                                    >
                                    </t-form-select>
                                    <form-errors :errors="form.errors" field="market_id"></form-errors>
                                </div>

                                <!-- Business Address -->
                                <div class="form-group">
                                    <label for="billing_address">Business Address</label>
                                    <input type="text" class="form-control" id="billing_address"
                                           v-model="form.address" />
                                    <form-errors :errors="form.errors" field="address"></form-errors>
                                </div>

                                <!-- Business Address Line 2 -->
                                <div class="form-group">
                                    <label for="billing_address_line_2">Business Address Line 2 <small
                                        class="ml-2 optional">Optional</small></label>
                                    <input type="text" class="form-control" id="billing_address_line_2"
                                           v-model="form.address_line_2" />
                                    <form-errors :errors="form.errors" field="address_line_2"></form-errors>
                                </div>

                                <!-- Country -->
                                <div class="form-group">
                                    <label for="billing_country">Country</label>
                                    <country-picker
                                        id="billing_country"
                                        name="billing_country"
                                        placeholder="Country"
                                        v-model="form.country"
                                    />
                                    <form-errors :errors="form.errors" field="country"></form-errors>
                                </div>

                                <!-- State and Zip -->
                                <div class="form-row">
                                    <div class="col">
                                        <label for="billing_state">Province/State/County</label>
                                        <state-picker
                                            :country="form.country"
                                            id="billing_state"
                                            name="billing_state"
                                            placeholder="State"
                                            v-model="form.state"
                                        />
                                        <form-errors :errors="form.errors" field="state"></form-errors>
                                    </div>
                                    <div class="col">
                                        <div class="form-group">
                                            <label for="billing_zip">Zip</label>
                                            <input type="text" class="form-control" id="billing_zip"
                                                   v-model="form.zip" />
                                            <form-errors :errors="form.errors" field="zip"></form-errors>
                                        </div>
                                    </div>
                                </div>

                                <!-- Timezone -->
                                <div class="form-group">
                                    <label for="timezone">Time Zone</label>
                                    <t-form-select
                                        group-label="region"
                                        group-options="zones"
                                        id="timezone"
                                        name="timezone"
                                        placeholder="Timezone"
                                        v-model="form.timezone"
                                        :options="timezoneOptions"
                                    >
                                    </t-form-select>
                                    <form-errors :errors="form.errors" field="timezone"></form-errors>
                                </div>

                                <!-- Website -->
                                <div class="form-group">
                                    <label for="website">Website <small class="ml-2 optional">Optional</small></label>
                                    <input type="text" class="form-control" id="website"
                                           v-model="form.website" />
                                    <form-errors :errors="form.errors" field="website"></form-errors>
                                </div>

                                <!-- Team Demographics -->
                                <h3>Team Demographics <small class="optional ml-2">Optional</small></h3>
                                <p class="text-muted">Team Demographics entered will post with all live asset
                                    listings.</p>

                                <div class="form-group">
                                    <label for="average_homegame_attendance">Average Homegame Attendance</label>
                                    <input type="number" class="form-control" id="average_homegame_attendance"
                                           v-model="form.settings.demographics.average_homegame_attendance" />
                                    <form-errors :errors="form.errors"
                                                 field="settings.demographics.average_homegame_attendance"></form-errors>
                                </div>

                                <div class="form-group">
                                    <label for="venue_capacity">Venue Capacity</label>
                                    <input type="number" class="form-control" id="venue_capacity"
                                           v-model="form.settings.demographics.venue_capacity" />
                                    <form-errors :errors="form.errors"
                                                 field="settings.demographics.venue_capacity"></form-errors>
                                </div>

                                <!-- Social Media Followers -->
                                <div class="form-group mb-0">
                                    <label>Social Media Followers</label>
                                </div>
                                <div class="form-row">
                                    <div class="col">
                                        <div class="form-group">
                                            <label for="twitter_followers">Twitter</label>
                                            <input type="number" class="form-control" id="twitter_followers"
                                                   v-model="form.settings.demographics.twitter_followers" />
                                            <form-errors :errors="form.errors"
                                                         field="settings.demographics.twitter_followers"></form-errors>
                                        </div>
                                    </div>
                                    <div class="col">
                                        <div class="form-group">
                                            <label for="facebook_followers">Facebook</label>
                                            <input type="number" class="form-control" id="facebook_followers"
                                                   v-model="form.settings.demographics.facebook_followers" />
                                            <form-errors :errors="form.errors"
                                                         field="settings.demographics.facebook_followers"></form-errors>
                                        </div>
                                    </div>
                                    <div class="col">
                                        <div class="form-group">
                                            <label for="snapchat_followers">Snapchat</label>
                                            <input type="number" class="form-control" id="snapchat_followers"
                                                   v-model="form.settings.demographics.snapchat_followers" />
                                            <form-errors :errors="form.errors"
                                                         field="settings.demographics.snapchat_followers"></form-errors>
                                        </div>
                                    </div>
                                    <div class="col">
                                        <div class="form-group">
                                            <label for="instagram_followers">Instagram</label>
                                            <input type="number" class="form-control" id="instagram_followers"
                                                   v-model="form.settings.demographics.instagram_followers" />
                                            <form-errors :errors="form.errors"
                                                         field="settings.demographics.instagram_followers"></form-errors>
                                        </div>
                                    </div>
                                </div>

                                <div class="form-group">
                                    <label for="average_website_impressions">Average Website Impressions</label>
                                    <input type="number" class="form-control" id="average_website_impressions"
                                           v-model="form.settings.demographics.average_website_impressions" />
                                    <form-errors :errors="form.errors"
                                                 field="settings.demographics.average_website_impressions"></form-errors>
                                </div>

                                <div class="form-group">
                                    <label for="average_tv_viewership">Average TV Viewership</label>
                                    <input type="number" class="form-control" id="average_tv_viewership"
                                           v-model="form.settings.demographics.average_tv_viewership" />
                                    <form-errors :errors="form.errors"
                                                 field="settings.demographics.average_tv_viewership"></form-errors>
                                </div>

                                <div class="form-group">
                                    <label for="percentage_male_fans">% Male Fans</label>
                                    <input type="number" class="form-control" id="percentage_male_fans"
                                           v-model="form.settings.demographics.percentage_male_fans" />
                                    <form-errors :errors="form.errors"
                                                 field="settings.demographics.percentage_male_fans"></form-errors>
                                </div>

                                <div class="form-group">
                                    <label for="percentage_female_fans">% Female Fans</label>
                                    <input type="number" class="form-control" id="percentage_female_fans"
                                           v-model="form.settings.demographics.percentage_female_fans" />
                                    <form-errors :errors="form.errors"
                                                 field="settings.demographics.percentage_female_fans"></form-errors>
                                </div>

                                <div class="form-group">
                                    <label for="average_age_range_of_fans">Average Age Range of Fans</label>
                                    <input type="number" class="form-control" id="average_age_range_of_fans"
                                           v-model="form.settings.demographics.average_age_range_of_fans" />
                                    <form-errors :errors="form.errors"
                                                 field="settings.demographics.average_age_range_of_fans"></form-errors>
                                </div>

                                <div class="form-group">
                                    <label for="household_income_of_fans">Household Income of Fans</label>
                                    <input type="number" class="form-control" id="household_income_of_fans"
                                           v-model="form.settings.demographics.household_income_of_fans" />
                                    <form-errors :errors="form.errors"
                                                 field="settings.demographics.household_income_of_fans"></form-errors>
                                </div>

                                <div class="form-group">
                                    <label for="average_education_level_of_fans">Average Education Level Completed by
                                        Fans</label>
                                    <input type="number" class="form-control" id="average_education_level_of_fans"
                                           v-model="form.settings.demographics.average_education_level_of_fans" />
                                    <form-errors :errors="form.errors"
                                                 field="settings.demographics.average_education_level_of_fans"></form-errors>
                                </div>

                                <div class="form-group">
                                    <label for="team_description">Team Description</label>
                                    <textarea class="form-control" id="team_description"
                                              v-model="form.settings.demographics.team_description"></textarea>
                                    <form-errors :errors="form.errors"
                                                 field="settings.demographics.team_description"></form-errors>
                                </div>

                                <!-- Fiscal Year -->
                                <div class="form-group">
                                    <label>Fiscal Year</label>
                                    <small class="form-text text-muted">These dates define information presented on your
                                        dashboard and all site calculations for the present fiscal year.</small>

                                    <div class="pl-2">
                                        <div class="form-group">
                                            <label>From</label>
                                            <div class="form-row">
                                                <div class="col">
                                                    <t-month-picker
                                                        id="startMonth"
                                                        v-model="form.schedule.start_month"
                                                        placeholder="Select a month">
                                                    </t-month-picker>
                                                    <form-errors :errors="form.errors"
                                                                 field="schedule.start_month"></form-errors>
                                                </div>
                                                <div class="col">
                                                    <t-day-picker
                                                        id="startDay"
                                                        v-model="form.schedule.start_day"
                                                        :month-picker="form.schedule.start_month"
                                                        placeholder="Select a day">
                                                    </t-day-picker>
                                                    <form-errors :errors="form.errors"
                                                                 field="schedule.start_day"></form-errors>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="form-group mt-2">
                                            <label>To</label>
                                            <div class="form-row">
                                                <div class="col">
                                                    <t-month-picker
                                                        id="endMonth"
                                                        v-model="form.schedule.end_month"
                                                        placeholder="Select a month">
                                                    </t-month-picker>
                                                    <form-errors :errors="form.errors"
                                                                 field="schedule.end_month"></form-errors>
                                                </div>
                                                <div class="col">
                                                    <t-day-picker
                                                        id="endDay"
                                                        v-model="form.schedule.end_day"
                                                        :month-picker="form.schedule.end_month"
                                                        placeholder="Select a day">
                                                    </t-day-picker>
                                                    <form-errors :errors="form.errors"
                                                                 field="schedule.end_day"></form-errors>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="form-group">
                                    <label for="revenue_goal">Online Revenue Goal</label>
                                    <div class="input-group">
                                        <div class="input-group-prepend">
                                            <span class="input-group-text">$</span>
                                        </div>
                                        <input type="number" class="form-control" id="revenue_goal"
                                               v-model="form.schedule.revenue_goal" />
                                    </div>
                                    <form-errors :errors="form.errors" field="schedule.revenue_goal"></form-errors>
                                </div>

                                <!-- Industries -->
                                <div class="form-group">
                                    <label for="industries">Industries</label>
                                    <t-form-select
                                        id="industries"
                                        name="industries"
                                        v-model="form.industries"
                                        :options="industryObjectOptions"
                                        multiple
                                        placeholder="Select Industries"
                                        @input="handleIndustriesChange"
                                    >
                                    </t-form-select>
                                    <form-errors :errors="form.errors" field="industries"></form-errors>
                                </div>

                                <!-- Industry Advertisers -->
                                <div v-if="Object.keys(form.industry_advertisers).length > 0">
                                    <div v-for="industryId in Object.keys(form.industry_advertisers)"
                                         :key="`industry-${industryId}`"
                                         class="pl-5">
                                        <div class="form-group">
                                            <label :for="`industry_advertiser_${industryId}`">{{
                                                    getIndustryName(industryId)
                                                }} Exclusive Advertiser</label>
                                            <input type="text" class="form-control"
                                                   :id="`industry_advertiser_${industryId}`"
                                                   v-model="form.industry_advertisers[industryId].advertiser_name"
                                                   placeholder="Advertiser Name" />
                                            <form-errors :errors="form.errors"
                                                         :field="`industry_advertisers.${industryId}.advertiser_name`"></form-errors>
                                        </div>
                                    </div>
                                </div>

                            </div>

                            <div class="form-group">
                                <button type="submit" class="btn btn-primary" :disabled="form.busy">
                                    {{ form.busy ? 'Updating...' : 'Update' }}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import get from 'lodash/get';
import CountryPicker from '@/shared/CountryPicker.vue';
import StatePicker from '@/shared/StatePicker.vue';
import { FormErrors } from '@jgile/laravel-vue';
import toString from 'lodash/toString';

export default {
    components: {
        CountryPicker,
        StatePicker,
        FormErrors,
    },
    props: {
        team: {
            required: true,
            type: Object,
        },
        user: {
            required: true,
        },
        timezoneOptions: {
            required: true,
        },
        marketOptions: {
            type: Object,
            default: () => [],
        },
        industryOptions: {
            type: Object,
            default: () => [],
        },
    },
    data() {
        return {
            form: new SparkForm(this.getInitialFormData()),
        };
    },
    computed: {
        isAdvertiser() {
            return this.team.team_type === 1;
        },
        teamTypeLabel() {
            if (this.isAdvertiser) {
                return 'Update Advertiser Profile';
            }
            return 'Update Team Profile';
        },
        teamNameLabel() {
            if (this.isAdvertiser) {
                return 'Advertiser Name';
            }
            return 'Team/Venue Name';
        },
        industryObjectOptions() {
            // Transform industryOptions object {id: "name"} to array [{id: id, name: "name"}]
            return Object.entries(this.industryOptions).map(([id, name]) => ({
                id: parseInt(id),
                name: name,
            }));
        },
    },
    watch: {
        'form.industries': {
            handler(newIndustries, oldIndustries) {
                this.updateIndustryAdvertisers(newIndustries);
            },
            deep: true,
            immediate: false,
        },
    },
    mounted() {

        // Initialize industry advertisers from existing data
        this.initializeIndustryAdvertisers();
    },
    methods: {
        getInitialFormData() {
            // Transform industries relationship data to form format
            let industries = [];
            let industryAdvertisers = {};

            if (this.team.industries && this.team.industries.length > 0) {
                this.team.industries.forEach(industry => {
                    industries.push(industry.id);
                    industryAdvertisers[industry.id] = {
                        advertiser_name: industry.pivot?.advertiser_name || '',
                        industry_name: industry.name || '',
                    };
                });
            }


            const baseFormData = {
                name: this.team.name || '',
                market_id: this.team.market_id || '',
                timezone: this.team.timezone || '',
                website: this.team.website || '',
                address: this.team.billing_address || '',
                address_line_2: this.team.billing_address_line_2 || '',
                city: this.team.billing_city || '',
                state: this.team.billing_state || '',
                zip: this.team.billing_zip || '',
                country: this.team.billing_country || '',
                industry_id: this.team.industry_id || '',
                schedule: {
                    revenue_goal: toString(get(this.team, 'current_schedule.revenue_goal', '')),
                    start_month: toString(get(this.team, 'current_schedule.start_month', '')),
                    start_day: toString(get(this.team, 'current_schedule.start_day', '')),
                    end_month: toString(get(this.team, 'current_schedule.end_month', '')),
                    end_day: toString(get(this.team, 'current_schedule.end_day', '')),
                },
                industries: industries,
                industry_advertisers: industryAdvertisers,
                settings: {
                    demographics: {
                        average_homegame_attendance: get(this.team, 'settings.demographics.average_homegame_attendance', ''),
                        venue_capacity: get(this.team, 'settings.demographics.venue_capacity', ''),
                        twitter_followers: get(this.team, 'settings.demographics.twitter_followers', ''),
                        facebook_followers: get(this.team, 'settings.demographics.facebook_followers', ''),
                        snapchat_followers: get(this.team, 'settings.demographics.snapchat_followers', ''),
                        instagram_followers: get(this.team, 'settings.demographics.instagram_followers', ''),
                        average_website_impressions: get(this.team, 'settings.demographics.average_website_impressions', ''),
                        average_tv_viewership: get(this.team, 'settings.demographics.average_tv_viewership', ''),
                        percentage_male_fans: get(this.team, 'settings.demographics.percentage_male_fans', ''),
                        percentage_female_fans: get(this.team, 'settings.demographics.percentage_female_fans', ''),
                        average_age_range_of_fans: get(this.team, 'settings.demographics.average_age_range_of_fans', ''),
                        household_income_of_fans: get(this.team, 'settings.demographics.household_income_of_fans', ''),
                        average_education_level_of_fans: get(this.team, 'settings.demographics.average_education_level_of_fans', ''),
                        team_description: get(this.team, 'settings.demographics.team_description', ''),
                    },
                },
            };

            console.log(this.team);

            return baseFormData;
        },

        initializeIndustryAdvertisers() {
            if (this.form.industries && this.form.industries.length > 0) {
                this.form.industries.forEach(industryId => {
                    if (!this.form.industry_advertisers[industryId]) {
                        this.$set(this.form.industry_advertisers, industryId, {
                            advertiser_name: '',
                            industry_name: this.getIndustryName(industryId),
                        });
                    }
                });
            }
        },

        getIndustryName(industryId) {
            const industry = this.industryObjectOptions.find(opt => opt.id === parseInt(industryId));
            return industry ? industry.name : '';
        },

        prepareFormDataForSubmission() {
            let formData = { ...this.form };

            // Transform industries to match API expectations (only for teams, not advertisers)
            if (!this.isAdvertiser && formData.industries && formData.industries.length > 0) {
                formData.industries = formData.industries.map(industryId => ({
                    id: industryId,
                    advertiser_name: get(formData.industry_advertisers, `${industryId}.advertiser_name`, ''),
                }));
            }

            return formData;
        },

        handleIndustriesChange(newIndustries) {
            // Manually trigger the same logic as the watcher
            this.updateIndustryAdvertisers(newIndustries);
        },

        updateIndustryAdvertisers(newIndustries) {
            // Ensure we have an array
            const industries = newIndustries || [];

            // Convert all IDs to strings for consistent comparison
            const industryIds = industries.map(id => String(id));

            // Add new industry advertisers
            industryIds.forEach(industryId => {
                if (!this.form.industry_advertisers.hasOwnProperty(industryId)) {
                    this.$set(this.form.industry_advertisers, industryId, {
                        advertiser_name: '',
                        industry_name: this.getIndustryName(parseInt(industryId)),
                    });
                }
            });

            // Remove industry advertisers that are no longer selected
            Object.keys(this.form.industry_advertisers).forEach(industryId => {
                if (!industryIds.includes(String(industryId))) {
                    this.$delete(this.form.industry_advertisers, industryId);
                }
            });

            // Force Vue to update
            this.$forceUpdate();
        },

        save() {
            const formData = this.prepareFormDataForSubmission();
            Spark.put(`/api/teams/${this.team.id}/settings`, formData)
                .then(() => {
                    // Show success message
                    this.$emit('team-updated');
                });
        },
    },
};
</script>
